import React, { memo, type RefCallback, useReducer, useState, useMemo, useCallback } from 'react';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { useIsEligibleForLiveConversationsActivityStatusSsr } from '@jira/customer-service__live-conversations-activity-status/src/useIsEligibleForLiveConversationsActivityStatus.tsx';
import { graphql, useFragment } from 'react-relay';
import { LiveConversationsActivityStatusIndicator } from '@jira/customer-service__live-conversations-activity-status/src/LiveConversationsActivityStatusIndicator.tsx';
import { convertToProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import { AsyncWorkforceAvailabilityStatusIndicator } from '@jira/servicedesk__workforce-availability-global-menu/src/AsyncWorkforceAvailabilityStatusIndicator.tsx';
import { useIsEligibleForWorkforceAvailabilityBadge } from '@jira/servicedesk__workforce-availability-global-menu/src/useIsEligibleForWorkforceAvailabilityBadge.tsx';
import type { LiveConversationsActivityStatusIndicator_query$key } from '@jira/customer-service__live-conversations-activity-status/src/__generated__/LiveConversationsActivityStatusIndicator_query.graphql.ts';
import type { TriggerProps } from '@atlaskit/popup/types';
import Popup from '@atlaskit/popup';
import type { ProfileProps } from '@atlaskit/atlassian-navigation/profile/types';
import Spinner from '@atlaskit/spinner/spinner';
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';

import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { useIsClientReady } from '@jira/ssr__ssr-toolkit/src/hooks/use-is-client-ready.tsx';
import UFOLabel from '@jira/platform__app-framework-ufo-label/src/index.tsx';
import { AccountMenuTrigger as UnmemoizedAccountMenuTrigger } from '@atlassian/account-menu/nav4/account-menu-trigger';
import { JiraEntryPointContainer } from '@jira/platform__entry-points-container/src/index.tsx';

// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import { useEntryPoint } from '@jira/platform__entry-point/src/controllers/use-entry-point/index.tsx';
import { useEntryPointButtonTrigger } from '@jira/platform__entry-point-button-trigger/src/index.tsx';
import { Nav4ProfileMenuEntryPoint } from '@jira/navigation-apps__atlassian-navigation-nav4-profile-content/src/Nav4ProfileMenuEntryPoint.entrypoint.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import { fg } from '@atlassian/jira-feature-gating';
import { memoWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/memo-with-condition/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';

import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge/src';
import type { AccountMenu_triggerQuery$key } from './__generated__/AccountMenu_triggerQuery.graphql.ts';

type AccountMenuProps = Pick<ProfileProps, 'onClick' | 'testId'> & {
	picture: string | undefined;
	email: string | undefined;
	accountMenuTriggerQueryFragment: AccountMenu_triggerQuery$key;
};

type MenuState = {
	isOpen: boolean;
	jsmStatusFetchKey: number;
};

type MenuAction = { type: 'TOGGLE' } | { type: 'CLOSE' };

function menuReducer(state: MenuState, action: MenuAction): MenuState {
	switch (action.type) {
		case 'TOGGLE':
			if (state.isOpen) {
				return { ...state, isOpen: false };
			}
			return { isOpen: true, jsmStatusFetchKey: state.jsmStatusFetchKey + 1 };
		case 'CLOSE':
			return { ...state, isOpen: false };
		default:
			return state;
	}
}

type AccountMenuStateController = {
	isOpen: boolean;
	jsmStatusFetchKey: number;
	handleTriggerClick: () => void;
	handleClose: () => void;
};

const useAccountMenuStateWithUseState = (): AccountMenuStateController => {
	const [isOpen, setIsOpen] = useState(false);

	const handleTriggerClick = useCallback(() => {
		setIsOpen((currentIsOpen) => !currentIsOpen);
	}, []);

	const handleClose = useCallback(() => {
		setIsOpen(false);
	}, []);

	return {
		isOpen,
		jsmStatusFetchKey: 0,
		handleTriggerClick,
		handleClose,
	};
};

const useAccountMenuStateWithReducer = (): AccountMenuStateController => {
	const [{ isOpen, jsmStatusFetchKey }, dispatch] = useReducer(menuReducer, {
		isOpen: false,
		jsmStatusFetchKey: -1,
	});

	const handleTriggerClick = useCallback(() => {
		dispatch({ type: 'TOGGLE' });
	}, []);

	const handleClose = useCallback(() => {
		dispatch({ type: 'CLOSE' });
	}, []);

	return {
		isOpen,
		jsmStatusFetchKey,
		handleTriggerClick,
		handleClose,
	};
};

const isWorkforceAemEnabled = (): boolean =>
	fg('jsm_workforce_use_aem') ? isFeatureEnabled('jira-service-management', 'jsm-workforce') : true;

const useAccountMenuState = functionWithCondition(
	() =>
		// eslint-disable-next-line jira/ff/no-preconditioning -- Main WFO gate must block status-control UI/API access when the parent feature is off.
		isWorkforceAemEnabled() &&
		fg('jsm_workforce_optimization_main') &&
		expVal('jsm_workforce_optimization_status_control', 'isEnabled', false),
	useAccountMenuStateWithReducer,
	useAccountMenuStateWithUseState,
);

export const AccountMenu: React.ComponentType<AccountMenuProps> = memo(
	({ testId, picture, email, accountMenuTriggerQueryFragment }) => {
		const { isOpen, jsmStatusFetchKey, handleTriggerClick, handleClose } = useAccountMenuState();
		const atlassianAccountId = useAccountId();

		const entryPointParams = useMemo(
			() => ({
				atlassianAccountId: atlassianAccountId ?? '',
				jiraOnboardingHubGateEnabled: fg('jira-onboarding-hub-gate') ?? false,
			}),
			[atlassianAccountId],
		);

		const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
			Nav4ProfileMenuEntryPoint,
			entryPointParams,
		);
		const triggerRef = useEntryPointButtonTrigger(entryPointActions, !isOpen);

		const runtimeProps = useMemo(
			() => ({
				onClosePopup: handleClose,
				testId,
				accountId: fg('shared-user-preferences') ? atlassianAccountId : undefined,
				jsmStatusFetchKey,
			}),
			[testId, atlassianAccountId, jsmStatusFetchKey, handleClose],
		);

		const TriggerInternal = useCallback(
			(triggerProps: TriggerProps) => {
				return (
					<Trigger
						triggerRef={triggerRef}
						onTriggerClick={handleTriggerClick}
						testId={testId}
						picture={picture}
						email={email}
						isOpen={isOpen}
						triggerProps={triggerProps}
						accountMenuTriggerQueryFragment={accountMenuTriggerQueryFragment}
					/>
				);
			},
			[
				triggerRef,
				handleTriggerClick,
				testId,
				picture,
				email,
				isOpen,
				accountMenuTriggerQueryFragment,
			],
		);

		return (
			<Popup
				isOpen={isOpen}
				onClose={handleClose}
				testId={testId}
				placement="bottom"
				trigger={TriggerInternal}
				content={() => (
					<JiraEntryPointContainer
						entryPointReferenceSubject={entryPointReferenceSubject}
						id="atlassian-navigation-profile-menu"
						packageName="atlassian-navigation-profile-menu"
						errorFallback="flag"
						fallback={
							<Box xcss={Nav4AccountMenuContentSkeletonStyles} padding="space.100">
								<Spinner />
							</Box>
						}
						runtimeProps={runtimeProps}
					/>
				)}
				shouldRenderToParent={fg('set-should-render-to-parent-to-true-theme-switcher')}
			/>
		);
	},
);

const AccountMenuTrigger = memoWithCondition(
	() => fg('jfp-magma-stability-fixes-nav-misc'),
	UnmemoizedAccountMenuTrigger,
);

function Trigger({
	triggerRef,
	onTriggerClick,
	testId,
	picture,
	email,
	isOpen,
	triggerProps,
	accountMenuTriggerQueryFragment,
}: {
	triggerProps: TriggerProps;
	triggerRef: RefCallback<HTMLElement>;
	onTriggerClick: () => void;
	testId: string | undefined;
	picture: string | undefined;
	email: string | undefined;
	isOpen: boolean;
	accountMenuTriggerQueryFragment: AccountMenu_triggerQuery$key;
}) {
	const ref = useCallback(
		(element: HTMLElement | null) => {
			triggerRef(element);
			// oxlint-disable-next-line typescript/consistent-type-assertions
			(triggerProps.ref as RefCallback<HTMLElement>)(element);
		},
		// oxlint-disable-next-line react-hooks/exhaustive-deps
		[triggerRef, triggerProps.ref],
	);

	const trigger = (
		<AccountMenuTrigger
			ref={ref}
			onClick={onTriggerClick}
			testId={testId && `${testId}--trigger`}
			picture={picture}
			trustPicture
			email={email}
			aria-expanded={isOpen}
			aria-haspopup
			aria-controls={testId && `${testId}--content`}
			data-ds--level={triggerProps['data-ds--level']}
		/>
	);

	return (
		<>
			{fg('csm-omni-channel-live-chat-in-widget') ||
			// eslint-disable-next-line jira/ff/no-preconditioning -- Main WFO gate must block status-control UI/API access when the parent feature is off.
			(isWorkforceAemEnabled() &&
				fg('jsm_workforce_optimization_main') &&
				expVal('jsm_workforce_optimization_status_control', 'isEnabled', false)) ? (
				<TriggerContent
					trigger={trigger}
					accountMenuTriggerQueryFragment={accountMenuTriggerQueryFragment}
				/>
			) : (
				trigger
			)}
		</>
	);
}

function TriggerContent({
	trigger,
	accountMenuTriggerQueryFragment,
}: {
	trigger: React.ReactNode;
	accountMenuTriggerQueryFragment: AccountMenu_triggerQuery$key;
}) {
	const queryData = useFragment<AccountMenu_triggerQuery$key>(
		graphql`
			fragment AccountMenu_triggerQuery on Query {
				jira_projectByIdOrKey(cloudId: $cloudId, idOrKey: $projectIdOrKey)
					@include(if: $shouldFetchProjectType) {
					projectType # note: remove projectType when cleaning csm_appification_standalone_navigation
				}
				...LiveConversationsActivityStatusIndicator_query
					@include(if: $shouldFetchCSMLiveChatAgent)
					@alias(as: "csmLiveChat")
			}
		`,
		accountMenuTriggerQueryFragment,
	);

	const projectType = queryData.jira_projectByIdOrKey?.projectType;

	const showCsmTrigger = useIsEligibleForLiveConversationsActivityStatusSsr(
		projectType ? convertToProjectType(projectType) : undefined,
	);

	if (showCsmTrigger) {
		return (
			<UFOLabel name="account-menu-trigger">
				<JSErrorBoundary
					id="TriggerWithLiveConversationsStatus"
					packageName="atlassian-navigation-profile-menu"
					teamName="boysenberry"
					fallback={() => trigger}
					sendToPrivacyUnsafeSplunk
				>
					<TriggerWithLiveConversationsStatus
						trigger={trigger}
						queryFragment={queryData.csmLiveChat}
					/>
				</JSErrorBoundary>
			</UFOLabel>
		);
	}

	if (
		// eslint-disable-next-line jira/ff/no-preconditioning -- Main WFO gate must block status-control UI/API access when the parent feature is off.
		isWorkforceAemEnabled() &&
		fg('jsm_workforce_optimization_main') &&
		expVal('jsm_workforce_optimization_status_control', 'isEnabled', false)
	) {
		return <JsmTriggerContent trigger={trigger} />;
	}

	return <>{trigger}</>;
}

function JsmTriggerContent({ trigger }: { trigger: React.ReactNode }) {
	const isClientReady = useIsClientReady();
	const showJsmTrigger = useIsEligibleForWorkforceAvailabilityBadge();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const handleMouseEnter = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'hovered',
				actionSubject: 'jsmProfileMenuTrigger',
			}),
			'profileMenu',
			{ source: 'profileMenu' },
		);
	}, [createAnalyticsEvent]);

	if (!showJsmTrigger) {
		return <>{trigger}</>;
	}

	return (
		<UFOLabel name="account-menu-trigger">
			<Box xcss={statusIndicatorWrapperStyles} onMouseEnter={handleMouseEnter}>
				{trigger}
				{isClientReady && <AsyncWorkforceAvailabilityStatusIndicator />}
			</Box>
		</UFOLabel>
	);
}

function TriggerWithLiveConversationsStatus({
	trigger,
	queryFragment,
}: {
	trigger: React.ReactNode;
	queryFragment?: LiveConversationsActivityStatusIndicator_query$key | null;
}) {
	return (
		<Box xcss={statusIndicatorWrapperStyles}>
			{trigger}
			<LiveConversationsActivityStatusIndicator queryFragment={queryFragment} />
		</Box>
	);
}

// These styles are defined to ensure that the initial popup for the AccountMenu content is the same width when
// displaying the skeleton loader as when displaying the actual content.
const Nav4AccountMenuContentSkeletonStyles = xcss({
	width: '304px',
	height: '307px',
});

const statusIndicatorWrapperStyles = xcss({
	position: 'relative',
	display: 'inline-block',
});
